/**
 * NV Cart Library - Main Entry Point
 * 
 * This is the modernized TypeScript version of the NVIDIA cart library,
 * refactored from nv-nav-cart-build.min.1.0.headless.js
 * 
 * @package nv-cart-lib
 * @version 2.0.0
 */

import './utils/silence-console-prod';

// Import styles
import './assets/styles/cart.styles.scss';

import { CartConfig, CartItem, CartState } from './types/cart.types';
import { nvstore, getCartState, dispatchCartAction } from './state/store';
import { actionTypes, CartStateInterface } from './state/actions/cartActions';
import { CurrencyFormatter } from './utils/currency.utils';
import { StorageUtils } from './utils/storage.utils';
import { ValidationUtils } from './utils/validation.utils';
import { DOMUtils } from './utils/dom.utils';
import { load } from './trigger';

export interface CartMountOptions {
  locale: string;
  mountId: string;
  program?: string;
  environment?: 'prod' | 'stg' | 'dev' | 'qa' | 'preview';
  [key: string]: any;
}

/**
 * Mount the cart UI using the same implementation as the legacy browser API.
 */
export function mountCart(options: CartMountOptions | null | undefined): void {
  console.log('[nvCart] mount called', options);
  // Validation
  if (options == null) {
    console.warn('[nvCart] mount skipped: options missing');
    return;
  }
  if (options.locale == null) {
    console.warn('[nvCart] mount skipped: locale missing');
    return;
  }
  if (options.mountId == null) {
    console.warn('[nvCart] mount skipped: mountId missing');
    return;
  } else {
    options.mountId = options.mountId === 'nv-cart-icon' ? 'nv-cart-item' : options.mountId;
  }
  if (typeof window === 'undefined') {
    console.warn('[nvCart] mount skipped: window is unavailable');
    return;
  }
  // Set global cart program
  try {
    (window as any).cartProgram = options.program || 'other';
  } catch (err) {
    (window as any).cartProgram = 'other';
  }
  // Call load function with mountId
  load(options);
}

/**
 * Main NVCart class - provides the public API
 * 
 * Note: This class exists for potential future modern API usage,
 * but the current production implementation uses window.nvCart.mount()
 * which is handled by the load() function and initCart.ts
 */
export class NVCart {
  private config: CartConfig;

  constructor(config: CartConfig) {
    this.config = config;
    console.warn('NVCart class instantiation is not fully implemented. Please use window.nvCart.mount() instead.');
  }

  // ========== Public API Methods ==========
  // Note: These methods are placeholders. Production code uses window.nvCart.mount()

  /**
   * Add item to cart
   */
  public addItem(item: CartItem): Promise<void> {
    if (!ValidationUtils.isValidCartItem(item)) {
      throw new Error('Invalid cart item');
    }
    nvstore.dispatch({
      type: actionTypes.ADD_ITEM_TO_CART,
      id: item.id,
      quantity: item.quantity,
      category: item.category || 'nvidia'
    });
    return Promise.resolve();
  }

  /**
   * Remove item from cart
   */
  public removeItem(itemId: string): Promise<void> {
    nvstore.dispatch({
      type: actionTypes.REMOVE_ITEM_FROM_CART,
      id: itemId
    });
    return Promise.resolve();
  }

  /**
   * Update item quantity
   */
  public updateQuantity(itemId: string, quantity: number): Promise<void> {
    if (!ValidationUtils.isValidQuantity(quantity)) {
      throw new Error('Invalid quantity');
    }
    const state = nvstore.getState();
    const item = state.cart.find((i: any) => i.id === itemId);
    if (item) {
      if (quantity > item.quantity) {
        nvstore.dispatch({
          type: actionTypes.INC_ITEM_QUANTITY,
          id: itemId,
          category: item.category
        });
      } else if (quantity < item.quantity) {
        nvstore.dispatch({
          type: actionTypes.DEC_ITEM_QUANTITY,
          id: itemId,
          category: item.category
        });
      }
    }
    return Promise.resolve();
  }

  /**
   * Clear all items from cart
   */
  public clearCart(): void {
    nvstore.dispatch({
      type: actionTypes.CLEAR_CART
    });
    StorageUtils.clearCartState();
  }

  /**
   * Get current cart state
   */
  public getState(): CartStateInterface {
    return nvstore.getState();
  }

  /**
   * Show cart UI
   */
  public showCart(): void {
    console.warn('showCart() not implemented. Cart UI is controlled by initCart.ts');
  }

  /**
   * Hide cart UI
   */
  public hideCart(): void {
    console.warn('hideCart() not implemented. Cart UI is controlled by initCart.ts');
  }

  /**
   * Get cart total price
   */
  public getTotal(): number {
    const state = nvstore.getState();
    return state.cart.reduce((total: number, item: any) => {
      return total + parseFloat(item.totalPrice || 0);
    }, 0);
  }

  /**
   * Get total number of items in cart
   */
  public getItemCount(): number {
    const state = nvstore.getState();
    return state.cart.reduce((count: number, item: any) => count + item.quantity, 0);
  }
}

// ========== Named Exports ==========

export {
  // State Management
  nvstore,
  getCartState,
  dispatchCartAction,
  actionTypes,


  // Utilities
  CurrencyFormatter,
  StorageUtils,
  ValidationUtils,
  DOMUtils,
};

// Export types separately for isolatedModules
export type {
  CartConfig,
  CartItem,
  CartState,
};

// ========== Default Export ==========

export default NVCart;

// ========== Global Window Interface (for legacy compatibility) ==========

declare global {
  interface Window {
    NVCart: typeof NVCart;
    nvCartInstance?: NVCart;
    nvCart?: {
      mount: (options: any) => void;
    };
  }
}

// Make available globally if needed
if (typeof window !== 'undefined') {
  window.NVCart = NVCart;

  // Legacy compatibility: mount() function pattern (following old library implementation)
  window.nvCart = {
    mount: function (options: any) {
      console.log('[nvCart] mount called', options);
      // Validation
      if (options == null) {
        console.warn('[nvCart] mount skipped: options missing');
        return;
      }
      if (options.locale == null) {
        console.warn('[nvCart] mount skipped: locale missing');
        return;
      }
      if (options.mountId == null) {
        console.warn('[nvCart] mount skipped: mountId missing');
        return;
      } else {
        options.mountId = options.mountId === 'nv-cart-icon' ? 'nv-cart-item' : options.mountId;
      }

      // Set global cart program
      try {
        (window as any).cartProgram = options.program || 'other';
      } catch (err) {
        (window as any).cartProgram = 'other';
      }

      // Call load function with mountId
      load(options);
    }
  };
}

